import { bootstrapApplication } from '@angular/platform-browser';
import { isDevMode } from '@angular/core';
import {
  CellStyleModule,
  CheckboxEditorModule,
  ClientSideRowModelApiModule,
  ClientSideRowModelModule,
  ColumnApiModule,
  ColumnAutoSizeModule,
  CsvExportModule,
  CustomEditorModule,
  CustomFilterModule,
  DateEditorModule,
  GridStateModule,
  HighlightChangesModule,
  LargeTextEditorModule,
  LocaleModule,
  ModuleRegistry,
  NumberEditorModule,
  NumberFilterModule,
  PaginationModule,
  PinnedRowModule,
  RenderApiModule,
  RowApiModule,
  RowAutoHeightModule,
  RowSelectionModule,
  RowStyleModule,
  SelectEditorModule,
  setupAgTestIds,
  TextEditorModule,
  TextFilterModule,
  TooltipModule,
  ValidationModule,
} from 'ag-grid-community';
import {
  ColumnsToolPanelModule,
  LicenseManager,
  MenuModule,
  MultiFilterModule,
  RichSelectModule,
  RowGroupingModule,
  SetFilterModule,
  StatusBarModule,
} from 'ag-grid-enterprise';
import { App } from './app/app';
import { appConfig } from './app/app.config';

/**
 * Preloads the app configuration before Angular bootstraps.
 * This ensures MSAL gets the real clientId during initialization,
 * avoiding the "client_id missing" error.
 */
async function preloadConfig(): Promise<void> {
  try {
    const response = await fetch(`/assets/app-config.json?t=${Date.now()}`);
    if (!response.ok) {
      throw new Error(`Config preload failed: ${response.status}`);
    }
    (window as any).__APP_CONFIG__ = await response.json();
  } catch (error) {
    console.error('Failed to preload app config.', error);
  }
}

LicenseManager.setLicenseKey(
  'Using_this_{AG_Grid}_Enterprise_key_{AG-120228}_in_excess_of_the_licence_granted_is_not_permitted___Please_report_misuse_to_legal@ag-grid.com___For_help_with_changing_this_key_please_contact_info@ag-grid.com___{Continental_Currency_Exchange}_is_granted_a_{Multiple_Applications}_Developer_License_for_{1}_Front-End_JavaScript_developer___All_Front-End_JavaScript_developers_need_to_be_licensed_in_addition_to_the_ones_working_with_{AG_Grid}_Enterprise___This_key_has_not_been_granted_a_Deployment_License_Add-on___This_key_works_with_{AG_Grid}_Enterprise_versions_released_before_{5_February_2027}____[v3]_[01]_MTgwMTc4NTYwMDAwMA==0713b35cec4f11feec67458820011a27',
);

// Register only the ag-grid modules needed for this app (reduces bundle size)
ModuleRegistry.registerModules([
  MultiFilterModule,
  // Community modules
  ClientSideRowModelModule,
  ColumnAutoSizeModule,
  CellStyleModule,
  TextFilterModule,
  NumberFilterModule,
  TooltipModule,
  CsvExportModule,
  RowSelectionModule,
  ValidationModule,
  RowStyleModule,
  PinnedRowModule,
  ClientSideRowModelApiModule,
  HighlightChangesModule,
  TextEditorModule,
  NumberEditorModule,
  DateEditorModule,
  CheckboxEditorModule,
  LargeTextEditorModule,
  SelectEditorModule,
  RichSelectModule,
  CustomEditorModule,
  CustomFilterModule,
  RowApiModule,
  RenderApiModule,
  ColumnApiModule,
  PaginationModule,
  RowAutoHeightModule,
  GridStateModule,
  // Enterprise modules
  ColumnsToolPanelModule,
  SetFilterModule,
  StatusBarModule,
  MenuModule,
  RowGroupingModule,
  LocaleModule,
]);

// AG Grid's own test-id support for grid-internal elements (cells, rows, headers) — dev-only, never in production
if (isDevMode()) {
  setupAgTestIds();
}

preloadConfig()
  .then(() => bootstrapApplication(App, appConfig))
  .catch((err) => console.error(err));
